import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Activity, Users, UserCheck, UserX, Percent, Receipt, IndianRupee } from "lucide-react";
import toastr from "toastr";

import useDocumentTitle from "../../hooks/useDocumentTitle";
import ShimmerLoader from "../../hooks/ShimmerLoader";
import downloadBlob from "../../utils/downloadBlob";
import { exportTransactors } from "../../services/api";
import { fetchActiveTransactors, fetchTransactorMembers } from "../../store/reportSlices/performanceReportSlice";
import { StatCard, ReportHeader, ErrorBanner, FilterActions, DrillModal, CountLink, inputCls, num, inr, pct, fmtDate, fmtDateTime, customerName } from "./ReportUI";

const DAY_OPTIONS = [7, 15, 30, 60, 90];
const buildPayload = (f) => ({ days: Number(f.days) || 30, program_id: f.programId ? Number(f.programId) : null });
const defaultFilters = () => ({ days: 30, programId: "" });

const DRILL_COLUMNS = [
    { header: "Customer", render: (r) => <span className="font-medium text-gray-800">{customerName(r)}</span> },
    { header: "Contact", render: (r) => <>{r.email || "—"}<br /><span className="text-xs text-gray-400">{r.mobile}</span></> },
    { header: "Tier", render: (r) => r.program_name },
    { header: "Valid till", render: (r) => fmtDate(r.deactivation_date) },
    { header: "Last transaction", render: (r) => fmtDateTime(r.last_txn_at) },
    { header: "Transactions", render: (r) => num(r.txn_count) },
    { header: "Order value", render: (r) => inr(r.order_value) },
];

export default function ActiveTransactors() {
    useDocumentTitle("Active Transactors | Reports");
    const dispatch = useDispatch();
    const { data, loading, error } = useSelector((s) => s.performanceReport.transactors);

    const [showFilters, setShowFilters] = useState(false);
    const [draft, setDraft] = useState(defaultFilters());
    const [applied, setApplied] = useState(defaultFilters());
    const [drill, setDrill] = useState(null); // { programId, programName, state }
    const [exporting, setExporting] = useState(false);

    const load = () => dispatch(fetchActiveTransactors(buildPayload(applied)));
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dispatch, applied]);

    const handleReset = () => { const f = defaultFilters(); setDraft(f); setApplied(f); };

    const drillPayload = () => ({ ...buildPayload(applied), program_id: drill?.programId || buildPayload(applied).program_id, state: drill?.state || null });

    const doExport = async (payload) => {
        setExporting(true);
        try { downloadBlob(await exportTransactors(payload), "active-members-transactions.csv"); }
        catch { toastr.error("Unable to export. Please try again."); }
        finally { setExporting(false); }
    };

    const s = data?.summary || { members: 0, transacted: 0, not_transacted: 0, rate: null, transactions: 0, order_value: 0 };
    const tiers = data?.tiers || [];
    const options = data?.tier_options || [];
    const days = data?.days || applied.days;
    const open = (t, state) => setDrill({ programId: t?.program_id || "", programName: t?.program_name || "All tiers", state });

    return (
        <div className="w-full space-y-4">
            <ReportHeader icon={Activity} title="Members Who Transacted"
                subtitle={`Active paid members with a completed transaction in the last ${days} days`}
                activeFilters={(Number(applied.days) !== 30 ? 1 : 0) + (applied.programId ? 1 : 0)}
                showFilters={showFilters} onToggleFilters={() => setShowFilters((v) => !v)}
                onExport={() => doExport(buildPayload(applied))} exporting={exporting} exportDisabled={loading || !s.members} />

            {showFilters && (
                <form onSubmit={(e) => { e.preventDefault(); setApplied({ ...draft }); }} className="bg-white rounded-xl shadow p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Transacted in the last</label>
                            <select value={draft.days} onChange={(e) => setDraft({ ...draft, days: e.target.value })} className={inputCls}>
                                {DAY_OPTIONS.map((d) => <option key={d} value={d}>{d} days</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Membership tier</label>
                            <select value={draft.programId} onChange={(e) => setDraft({ ...draft, programId: e.target.value })} className={inputCls}>
                                <option value="">All tiers</option>
                                {options.map((o) => <option key={o.program_id} value={o.program_id}>{o.program_name}</option>)}
                            </select>
                        </div>
                    </div>
                    <FilterActions onReset={handleReset} />
                </form>
            )}

            <ErrorBanner message={error} onRetry={load} />

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard icon={Users} label="Active Paid Members" value={num(s.members)} accent="blue" onClick={() => open(null, "")} />
                <StatCard icon={UserCheck} label={`Transacted (${days}d)`} value={num(s.transacted)} accent="green" onClick={() => open(null, "transacted")} />
                <StatCard icon={UserX} label="Not Transacted" value={num(s.not_transacted)} accent="rose" onClick={() => open(null, "not_transacted")} />
                <StatCard icon={Percent} label="Transacting Rate" value={pct(s.rate)} accent="amber" />
                <StatCard icon={Receipt} label="Transactions" value={num(s.transactions)} sub="by active members" accent="purple" />
                <StatCard icon={IndianRupee} label="Order Value" value={inr(s.order_value)} accent="slate" />
            </div>

            <div className="bg-white rounded-xl shadow overflow-x-auto">
                <div className="px-4 py-3 border-b"><h4 className="font-semibold text-gray-800">By membership tier</h4></div>
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs uppercase text-gray-500 bg-gray-50">
                            <th className="px-4 py-3">Membership Tier</th>
                            <th className="px-4 py-3 text-right">Active Members</th>
                            <th className="px-4 py-3 text-right">Transacted</th>
                            <th className="px-4 py-3 text-right">Not Transacted</th>
                            <th className="px-4 py-3 w-48">Rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && !data && <tr><td colSpan={5}><ShimmerLoader /><ShimmerLoader /></td></tr>}
                        {!loading && tiers.length === 0 && <tr><td colSpan={5} className="text-center text-gray-500 py-10">No paid membership tiers found.</td></tr>}
                        {tiers.map((t) => (
                            <tr key={t.program_id} className="border-t hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium text-gray-800">{t.program_name}</td>
                                <td className="px-4 py-3 text-right"><CountLink value={t.members} onClick={() => open(t, "")} className="text-gray-800" /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={t.transacted} onClick={() => open(t, "transacted")} className="text-green-600" /></td>
                                <td className="px-4 py-3 text-right"><CountLink value={t.not_transacted} onClick={() => open(t, "not_transacted")} className="text-rose-600" /></td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <div className="h-2 flex-1 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-green-500" style={{ width: `${t.rate || 0}%` }} /></div>
                                        <span className="text-xs text-gray-600 w-12 text-right">{pct(t.rate)}</span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {tiers.length > 0 && (
                        <tfoot>
                            <tr className="border-t-2 bg-gray-50 font-bold">
                                <td className="px-4 py-3">Total (unique customers)</td>
                                <td className="px-4 py-3 text-right">{num(s.members)}</td>
                                <td className="px-4 py-3 text-right">{num(s.transacted)}</td>
                                <td className="px-4 py-3 text-right">{num(s.not_transacted)}</td>
                                <td className="px-4 py-3 text-right text-gray-700">{pct(s.rate)}</td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {drill && (
                <DrillModal title={drill.programName}
                    subtitle={`${drill.state === "transacted" ? "Transacted" : drill.state === "not_transacted" ? "Did not transact" : "All active members"} · last ${days} days`}
                    listKey="transactorMembers" thunk={fetchTransactorMembers} payload={drillPayload()}
                    columns={DRILL_COLUMNS} onExport={() => doExport(drillPayload())} exporting={exporting} onClose={() => setDrill(null)} />
            )}
        </div>
    );
}